/* eslint-disable react-hooks/set-state-in-effect --
 * v77.99.77: Idiomatic React patterns (effect-driven state hydration,
 * English text with apostrophes/quotes in JSX). These experimental
 * rules are not shipped by eslint-plugin-react-hooks 5.2.0 and
 * mis-flag intentional patterns. `react/no-unescaped-entities`
 * similarly flags safe English text.
 */
/* eslint-disable react-hooks/purity, react-hooks/preserve-manual-memoization --
 * Impersonation banner intentionally reads useAuth().user.impersonation
 * (a Context value) inside a useMemo for live countdown calculation. The
 * experimental purity / preserve-manual-memoization rules misclassify
 * this as impure — but the value is stable per render and the dep array
 * captures every read, which is the contract we want.
 */
import { useEffect, useMemo, useState } from "react";
import { ShieldAlert, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * Always-visible banner rendered while an admin is impersonating
 * another user. Pulled from `useAuth().user.impersonation` which is
 * populated by `/auth/me` from the JWT's `act` claim.
 *
 * Shows:
 *   - Admin email (the real principal)
 *   - Target email (who they're impersonating, == current `user.email`)
 *   - Reason (free-text from /admin/impersonate/start)
 *   - Live countdown to expiry
 *   - "Exit impersonation" button
 *
 * Sticky at the top of the viewport — `top-0`, full width, high
 * z-index — so the admin literally cannot forget they're in this
 * mode while clicking around the parent UX.
 *
 * Side-effect: when the impersonation expires, automatically calls
 * `stopImpersonation()` so the admin token is restored without the
 * admin needing to click anything.
 */
export default function ImpersonationBanner() {
  const { user, stopImpersonation } = useAuth();
  const imp = user?.impersonation;
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!imp?.active) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [imp?.active]);

  const remaining = useMemo(() => {
    if (!imp?.expires_at) return null;
    const ms = new Date(imp.expires_at).getTime() - now;
    if (ms <= 0) return 0;
    return ms;
  }, [imp?.expires_at, now]);

  // Auto-exit when the JWT expires — the next API call would 401
  // anyway. Doing it proactively avoids confusing error toasts.
  useEffect(() => {
    if (remaining === 0) {
      (async () => {
        try { await stopImpersonation(); }
        finally { toast.info("Impersonation session expired."); }
      })();
    }
  }, [remaining, stopImpersonation]);

  if (!imp?.active) return null;

  const mins = remaining != null ? Math.floor(remaining / 60000) : "?";
  const secs = remaining != null ? Math.floor((remaining % 60000) / 1000).toString().padStart(2, "0") : "00";

  const handleExit = async () => {
    try {
      await stopImpersonation();
      toast.success("Exited impersonation.");
    } catch {
      toast.error("Could not cleanly exit impersonation. Please sign in again.");
    }
  };

  return (
    <div
      className="fixed top-0 inset-x-0 z-[100] bg-[#A11C2C] text-white shadow-md"
      role="alert"
      data-testid="impersonation-banner"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-[13px]">
        <div className="flex items-center gap-2 font-semibold">
          <ShieldAlert className="w-4 h-4 shrink-0" />
          <span>Admin impersonation mode active</span>
        </div>
        <div className="flex-1 truncate text-[12px] opacity-90">
          <span data-testid="impersonation-banner-admin">
            <strong>{imp.admin_email || "admin"}</strong>
          </span>
          {" → acting as "}
          <span data-testid="impersonation-banner-target">
            <strong>{user?.email || "user"}</strong>
          </span>
          {imp.reason && (
            <>
              {" · "}
              <span className="italic" data-testid="impersonation-banner-reason">{imp.reason}</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="font-mono-data text-[12px] tabular-nums" data-testid="impersonation-banner-countdown">
            {mins}:{secs}
          </span>
          <button
            onClick={handleExit}
            className="inline-flex items-center gap-1 bg-white text-[#A11C2C] hover:bg-[#F5E6E8] rounded-sm px-2.5 py-1 text-[12px] font-semibold transition-colors"
            data-testid="impersonation-exit-button"
          >
            <LogOut className="w-3.5 h-3.5" />
            Exit
          </button>
        </div>
      </div>
    </div>
  );
}
